import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { QueryDataState } from '@shared/interfaces';
import { UserCustomTheme, UserRole } from '@shared/types';
import { QueryStatus } from 'react-query';

import { Bugo } from '../bugo/bugo.interface';
import { BugoBrand } from '../bugoBrand/bugoBrand.interface';
import { Fevent } from '../fevent/fevent.interface';
import { Member } from '../member/member.interface';
import { ShopOrder } from '../shopOrder/shopOrder.interface';

//* 비밀번호가 설정된 경로 (백엔드 PasswordChangedVia 와 같은 값)
export type PasswordChangedVia = 'self' | 'admin' | 'admin-create' | 'signup' | 'upgrade';

export const PASSWORD_CHANGED_VIA_LABEL: Record<PasswordChangedVia, string> = {
  self: '본인',
  admin: '관리자',
  'admin-create': '생성',
  signup: '가입',
  upgrade: '전환',
};

//* Read 형 원형
export type User<
  WorkerBugoBrand = string,
  Fevents = undefined,
  AgencyBugoBrand = BugoBrand,
  ShopOrder = undefined,
> = {
  _id: string;
  username: string;
  password: string;
  roles: UserRole[];
  info: {
    name: string;
    gender: string;
    birth: string;
    phoneNumber: string;
    theme: UserCustomTheme;
    //* 본인이 SMS 인증으로 확인한 시각 (관리자 로그인 2단계 인증)
    phoneVerifiedAt?: Date | string;
  };
  //* 계정 보안 지표. 백엔드 스펙: smartbugo-api/docs/account-security.md
  security?: {
    //* 운영·스태프만 판정. undefined = 미판정(아직 로그인·변경한 적 없음)
    isWeakPassword?: boolean;
    weakPasswordCheckedAt?: Date | string;
    passwordChangedAt?: Date | string;
    passwordChangedVia?: PasswordChangedVia;
  };
  //* 마지막 토큰 발급(로그인) 시각
  lastLoginAt?: Date | string;
  //* 마지막 인증 요청(활동) 시각 — 운영·스태프만, 5분 스로틀
  lastActiveAt?: Date | string;
  //* 현재 토큰의 만료 시각
  tokenExpiresAt?: Date | string;
  bugoBrand: WorkerBugoBrand;
  //* GiftBrandSeller가 담당하는 답례품 브랜드들 (read시 populate되면 객체 배열, 1명이 여러 브랜드 가능)
  giftItemBrands?: (string | { _id: string; name: string })[];
  //* @deprecated 단일 브랜드 구필드 — 마이그레이션 완료 후 제거 예정 (읽기 폴백용으로만 유지)
  giftItemBrand?: string | { _id: string; name: string };
  isDeleted?: boolean;
  adminDetail?: {
    cemeteryList: string[];
    mournerTextList?: string[];
  };
  bugoAgencyAdminDetail?: {
    cemeteryList: string[];
    //* 자주쓰는 상주말씀(사용자 즐겨찾기). 프리셋은 GET public/fevent/mourner-text-presets
    //* 스펙: docs/bugo-mourner-text-favorites-spec.md
    mournerTextList?: string[];
    bugoBrands: AgencyBugoBrand[];
  };
  bugoAgencyBranchAdminDetail?: {
    teamType: string;
    teamName: string;
  };
  bugoAgencyWorkerDetail?: {
    cemeteryList: string[];
    mournerTextList?: string[];
    fevents: Fevents;
    region: string;
    teamType: string;
    teamName: string;
    giftPilot: boolean;
  };
  bugoFuneralHomeDetail?: {
    cemeteryList: string[];
    mournerTextList?: string[];
  };
  bugoCustomerDetail?: {
    cemeteryList: string[];
    mournerTextList?: string[];
  };
  bugoGuestDetail?: {
    cemeteryList: string[];
    mournerTextList?: string[];
  };

  storeCustomerDetail?: {
    shopOrders: ShopOrder[];
    shopMembershipPoint: number;
    bugoDetail: {
      condolencePhraseList: string[];
      senderPhraseList: string[];
    };
  };
  createdAt?: Date;
};

//* status포함한 state형
export type UserState = {
  status: QueryStatus;
  data: User | null;
};
export type WorkerUser = User<
  string,
  Fevent<
    string,
    FuneralHomeInfo,
    undefined,
    ShopOrder[],
    Member<string, Bugo>[],
    string,
    undefined,
    string,
    string,
    Bugo[]
  >[]
>;

export type WorkerUserState = QueryDataState<WorkerUser>;
export type WorkerUserListState = QueryDataState<WorkerUser[]>;

//* _id뺀 create형
export type UserCreate = Omit<User, '_id' | 'bugoBrand' | 'bugoAgencyWorkerDetail'>;

//* optional Patch형
export type UserUpdate = {
  _id: string;
  username?: string;
  password?: string;
  roles?: UserRole[];
  giftItemBrands?: string[];
  info?: {
    name?: string;
    gender?: string;
    birth?: string;
    phoneNumber?: string;
    theme?: UserCustomTheme;
  };
};

export type bugoAgencyWorkerUpdate<Fevents = undefined> = {
  _id: string;
  info?: {
    name?: string;
    gender?: string;
    birth?: string;
    phoneNumber?: string;
    theme?: UserCustomTheme;
  };
  isDeleted?: boolean;
  bugoAgencyWorkerDetail?: {
    cemeteryList?: string[];
    mournerTextList?: string[];
    fevents?: Fevents;
    region?: string;
    teamType?: string;
    teamName?: string;
    giftPilot?: boolean;
  };
};

//* PATCH auth/me/detail 로 보내는 역할별 detail 부분 갱신형.
//* 서버는 보낸 필드만 dotted $set 한다(cemeteryList만 보내도 mournerTextList는 남는다).
export type MyDetailListKey = 'cemeteryList' | 'mournerTextList';
export type MyDetailListUpdate = {
  _id: string;
} & Partial<
  Record<
    | 'bugoAgencyAdminDetail'
    | 'bugoAgencyWorkerDetail'
    | 'bugoFuneralHomeDetail'
    | 'bugoCustomerDetail'
    | 'bugoGuestDetail',
    Partial<Record<MyDetailListKey, string[]>> & Record<string, unknown>
  >
>;

export type GuestInfo = {
  name: string;
  phoneNumber: string;
  password: string;
};
